/**
 * @fileoverview Dollar Liquidity View Model Builder
 * Wires loaders and signals into a complete view model for UI consumption.
 * @module observatories/dollar_liquidity/view_model
 */

import { dollarLiquidityManifest } from "./manifest.js";
import { createEmptyViewModel } from "./types.js";
import { loadAllLiquiditySeries, getLiquidityDataMode } from "../../store/liquidity/index.js";
import {
  dxyTrendSignal,
  crossCurrencyBasisStressSignal,
  usdLiquidityYoySignal,
} from "../../signals/liquidity/index.js";

/**
 * View model status indicating data availability.
 * @typedef {"ready"|"partial"|"data-unavailable"|"error"} ViewModelStatus
 */

/**
 * Generates a narrative summary based on signal outputs.
 * @param {Object} dxyResult - DXY trend signal output
 * @param {Object} basisResult - Basis stress signal output
 * @param {Object} liquidityResult - USD liquidity YoY signal output
 * @returns {string} Narrative summary
 */
function generateNarrative(dxyResult, basisResult, liquidityResult) {
  const dxyDesc =
    dxyResult.trend === "up"
      ? `The dollar has strengthened ${Math.abs(dxyResult.pctChange).toFixed(1)}% over this period, creating headwinds for offshore borrowers.`
      : dxyResult.trend === "down"
      ? `The dollar has weakened ${Math.abs(dxyResult.pctChange).toFixed(1)}%, easing pressure on global USD debtors.`
      : `The dollar has been relatively stable, providing a neutral backdrop for global funding conditions.`;

  const basisDesc =
    basisResult.severity === "extreme"
      ? `Cross-currency basis swaps signal extreme stress at ${basisResult.currentBasis.toFixed(0)}bps, indicating severe dollar shortage.`
      : basisResult.severity === "high"
      ? `Basis swap spreads show elevated stress at ${basisResult.currentBasis.toFixed(0)}bps.`
      : basisResult.severity === "medium"
      ? `Basis conditions are moderately stressed at ${basisResult.currentBasis.toFixed(0)}bps.`
      : `Basis swap spreads are benign at ${basisResult.currentBasis.toFixed(0)}bps, suggesting ample dollar availability.`;

  const liquidityDesc =
    liquidityResult.direction === "expanding"
      ? `Global USD liquidity is expanding at ${liquidityResult.yoy.toFixed(1)}% YoY, supporting risk appetite.`
      : liquidityResult.direction === "contracting"
      ? `Global USD liquidity is contracting at ${liquidityResult.yoy.toFixed(1)}% YoY, a warning sign for risk assets.`
      : `Global USD liquidity growth is flat, neither tailwind nor headwind.`;

  return `${dxyDesc} ${basisDesc} ${liquidityDesc}`;
}

/**
 * Generates a fallback narrative when data is unavailable.
 * @param {Object} errors - Object containing error flags for each series
 * @returns {string} Fallback narrative
 */
function generateFallbackNarrative(errors) {
  const unavailable = Object.entries(errors)
    .filter(([, hasError]) => hasError)
    .map(([key]) => key);

  if (unavailable.length === 3) {
    return "Data is currently unavailable for all liquidity indicators. Please try again later or check your data source configuration.";
  }

  return `Some data is unavailable (${unavailable.join(", ")}). The displayed metrics may be incomplete.`;
}

/**
 * Builds the complete Dollar Liquidity Observatory view model.
 * 
 * @param {string} timeRange - Time range for data (e.g., "1Y", "6M")
 * @returns {Promise<import('./types.js').DollarLiquidityViewModel>} Complete view model
 */
export async function buildDollarLiquidityViewModel(timeRange) {
  // Track data availability
  const errors = {
    dxy: false,
    basis: false,
    liquidity: false,
  };

  // Load all series in parallel
  let dxy, basis, liquidity;
  try {
    const seriesBundle = await loadAllLiquiditySeries({ timeRange });
    dxy = seriesBundle.dxy;
    basis = seriesBundle.basis;
    liquidity = seriesBundle.liquidity;
  } catch (error) {
    console.error("[ViewModel] Error loading series:", error);
    // Return an error state view model
    const viewModel = createEmptyViewModel(timeRange);
    viewModel.status = "error";
    viewModel.error = error.message;
    viewModel.narrative = "Unable to load liquidity data. Please try again later.";
    return viewModel;
  }

  // Check for empty/invalid series
  if (!dxy || dxy.length === 0) errors.dxy = true;
  if (!basis || basis.length === 0) errors.basis = true;
  if (!liquidity || liquidity.length === 0) errors.liquidity = true;

  const hasAllData = !errors.dxy && !errors.basis && !errors.liquidity;
  const hasNoData = errors.dxy && errors.basis && errors.liquidity;

  // If no data at all, return early
  if (hasNoData) {
    const viewModel = createEmptyViewModel(timeRange);
    viewModel.status = "data-unavailable";
    viewModel.narrative = generateFallbackNarrative(errors);
    return viewModel;
  }

  // Compute signals (with error handling for each)
  let dxyResult = null;
  let basisResult = null;
  let liquidityResult = null;

  if (!errors.dxy) {
    try {
      dxyResult = dxyTrendSignal.compute({ series: dxy });
    } catch (e) {
      console.error("[ViewModel] DXY signal error:", e);
      errors.dxy = true;
    }
  }

  if (!errors.basis) {
    try {
      basisResult = crossCurrencyBasisStressSignal.compute({ series: basis });
    } catch (e) {
      console.error("[ViewModel] Basis signal error:", e);
      errors.basis = true;
    }
  }

  if (!errors.liquidity) {
    try {
      liquidityResult = usdLiquidityYoySignal.compute({ series: liquidity });
    } catch (e) {
      console.error("[ViewModel] Liquidity signal error:", e);
      errors.liquidity = true;
    }
  }

  // Build headline metrics (include what we have)
  const headlineMetrics = [];

  if (dxyResult) {
    headlineMetrics.push({
      id: "dxy_change",
      label: "DXY Change",
      value: dxyResult.pctChange,
      unit: "%",
      direction: dxyResult.trend,
      severity: dxyResult.trend === "up" ? "medium" : "low",
    });
  }

  if (basisResult) {
    headlineMetrics.push(
      {
        id: "basis_stress",
        label: "Basis Stress",
        value: basisResult.stressScore,
        unit: "/100",
        severity: basisResult.severity,
      },
      {
        id: "current_basis",
        label: "Current Basis",
        value: basisResult.currentBasis,
        unit: "bps",
        severity: basisResult.severity,
      }
    );
  }

  if (liquidityResult) {
    headlineMetrics.push({
      id: "liquidity_yoy",
      label: "USD Liquidity YoY",
      value: liquidityResult.yoy,
      unit: "%",
      direction: liquidityResult.direction === "expanding" ? "up" : 
                 liquidityResult.direction === "contracting" ? "down" : "flat",
      severity: liquidityResult.direction === "contracting" ? "high" : "low",
    });
  }

  // Build panels (include what we have)
  const panels = [];

  if (dxyResult && dxy.length > 0) {
    panels.push({
      id: "dxy_chart",
      type: "timeseries",
      title: "US Dollar Index (DXY)",
      description: "Tracks the strength of the US dollar against a basket of major currencies.",
      payload: {
        series: dxy,
        metadata: {
          startValue: dxyResult.startValue,
          endValue: dxyResult.endValue,
          trend: dxyResult.trend,
        },
      },
    });
  }

  if (basisResult && basis.length > 0) {
    panels.push({
      id: "basis_chart",
      type: "timeseries",
      title: "Cross-Currency Basis Swaps",
      description: "Measures the premium for accessing USD funding via swaps. Negative values indicate dollar shortage.",
      payload: {
        series: basis,
        metadata: {
          avgDeviation: basisResult.avgDeviation,
          stressScore: basisResult.stressScore,
          severity: basisResult.severity,
        },
      },
    });
  }

  if (liquidityResult && liquidity.length > 0) {
    panels.push({
      id: "liquidity_chart",
      type: "timeseries",
      title: "Global USD Liquidity",
      description: "Approximates total offshore USD credit and liquidity conditions.",
      payload: {
        series: liquidity,
        metadata: {
          yoy: liquidityResult.yoy,
          direction: liquidityResult.direction,
        },
      },
    });
  }

  // Generate narrative
  let narrative;
  if (hasAllData && dxyResult && basisResult && liquidityResult) {
    narrative = generateNarrative(dxyResult, basisResult, liquidityResult);
    panels.push({
      id: "summary_panel",
      type: "text",
      title: "Current Conditions",
      payload: {
        text: narrative,
      },
    });
  } else {
    narrative = generateFallbackNarrative(errors);
  }

  // Assemble view model
  const viewModel = createEmptyViewModel(timeRange);
  viewModel.headlineMetrics = headlineMetrics;
  viewModel.panels = panels;
  viewModel.narrative = narrative;
  viewModel.status = hasAllData ? "ready" : "partial";
  
  // Add data source info
  viewModel.dataSource = {
    mode: getLiquidityDataMode(),
    errors,
  };

  return viewModel;
}
